import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown } from 'lucide-react';
import { productCategories } from '../data/productCategories';

const Header = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isProductsOpen, setIsProductsOpen] = useState(false);
  const [isAboutOpen, setIsAboutOpen] = useState(false);
  const location = useLocation();

  const toggleMenu = () => {
    setIsOpen(!isOpen);
  };

  const closeMenu = () => {
    setIsOpen(false);
    setIsProductsOpen(false);
    setIsAboutOpen(false);
  };

  const isActive = (path) => {
    return location.pathname === path;
  };

  // Map product categories to navigation format
  const navigationCategories = productCategories.map(category => ({
    name: category.name,
    path: `/products?category=${encodeURIComponent(category.name)}`,
    description: category.description
  }));

  return (
    <header className="bg-white shadow-sm sticky top-0 z-50">
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center" onClick={closeMenu}>
            <img src="https://filedn.com/lRYNf7eGO3tp3VIi6pVnJX0/totalfilt/logo.png" alt="Minipore" className="h-8 w-auto mr-3" />
            <span className="text-xl font-bold text-gray-900">Minipore</span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-6">
            <Link to="/" className={`text-gray-700 hover:text-blue-600 transition-colors ${isActive('/') ? 'text-blue-600 font-medium' : ''}`} >
              Home
            </Link>

            {/* Products Dropdown */}
            <div className="relative group" onMouseEnter={() => setIsProductsOpen(true)} onMouseLeave={() => setIsProductsOpen(false)} >
              <Link to="/products" className={`flex items-center text-gray-700 hover:text-blue-600 transition-colors ${isActive('/products') || location.pathname.startsWith('/products/') ? 'text-blue-600 font-medium' : ''}`} >
                Products
                <ChevronDown className="ml-1 h-4 w-4" />
              </Link>
              {/* Dropdown Menu */}
              <div className={`absolute top-full left-0 mt-2 w-80 bg-white rounded-lg shadow-lg border border-gray-200 transition-all duration-200 ${isProductsOpen ? 'opacity-100 visible transform translate-y-0' : 'opacity-0 invisible transform translate-y-2'}`}>
                <div className="p-4">
                  <div className="mb-4">
                    <Link to="/products" className="block text-lg font-semibold text-gray-900 hover:text-blue-600 transition-colors mb-2" onClick={closeMenu} >
                      All Products
                    </Link>
                    <p className="text-sm text-gray-600">Browse our complete product catalog</p>
                  </div>
                  <div className="space-y-3 max-h-80 overflow-y-auto">
                    {navigationCategories.map((category) => (
                      <Link key={category.name} to={category.path} className="block p-3 rounded-lg hover:bg-gray-50 transition-colors group" onClick={closeMenu} >
                        <div className="font-medium text-gray-900 group-hover:text-blue-600 transition-colors">
                          {category.name}
                        </div>
                         <div className="text-sm text-gray-600 mt-1">
                          {category.description}
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* About Us Dropdown */}
            <div className="relative group" onMouseEnter={() => setIsAboutOpen(true)} onMouseLeave={() => setIsAboutOpen(false)}>
              <div className={`cursor-pointer flex items-center text-gray-700 hover:text-blue-600 transition-colors ${isActive('/about') || isActive('/culture') ? 'text-blue-600 font-medium' : ''}`}>
                About Us
                <ChevronDown className="ml-1 h-4 w-4" />
              </div>
              <div className={`absolute top-full left-1/2 -translate-x-1/2 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 transition-all duration-200 ${isAboutOpen ? 'opacity-100 visible transform translate-y-0' : 'opacity-0 invisible transform translate-y-2'}`}>
                <div className="p-2">
                  <Link to="/about" className="block w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-50 hover:text-blue-600 rounded-md transition-colors" onClick={closeMenu}>
                    About Minipore
                  </Link>
                  <Link to="/culture" className="block w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-50 hover:text-blue-600 rounded-md transition-colors" onClick={closeMenu}>
                    Our Culture
                  </Link>
                </div>
              </div>
            </div>

            <Link to="/contact" className={`text-gray-700 hover:text-blue-600 transition-colors ${isActive('/contact') ? 'text-blue-600 font-medium' : ''}`} >
              Contact
            </Link>
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden">
            <button onClick={toggleMenu} className="text-gray-700 hover:text-blue-600 focus:outline-none focus:text-blue-600" aria-label="Toggle menu" >
              {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        <div className={`md:hidden transition-all duration-300 ease-in-out ${isOpen ? 'max-h-[80vh] opacity-100 overflow-y-auto' : 'max-h-0 opacity-0 overflow-hidden'}`}>
          <div className="px-2 pt-2 pb-3 space-y-1 bg-white border-t border-gray-200">
            <Link to="/" className={`block px-3 py-2 text-base text-gray-700 hover:text-blue-600 hover:bg-gray-50 rounded-md transition-colors ${isActive('/') ? 'text-blue-600 bg-blue-50 font-medium' : ''}`} onClick={closeMenu} >
              Home
            </Link>

            {/* Mobile Products Section */}
            <div>
              <Link to="/products" className={`block px-3 py-2 text-base text-gray-700 hover:text-blue-600 hover:bg-gray-50 rounded-md transition-colors ${isActive('/products') || location.pathname.startsWith('/products/') ? 'text-blue-600 bg-blue-50 font-medium' : ''}`} onClick={closeMenu} >
                All Products
              </Link>
              {/* Product Categories */}
              <div className="ml-4 mt-2 space-y-1 border-l pl-4">
                {navigationCategories.map((category) => (
                  <Link key={category.name} to={category.path} className="block px-3 py-2 text-sm text-gray-600 hover:text-blue-600 hover:bg-gray-50 rounded-md transition-colors" onClick={closeMenu} >
                    {category.name}
                  </Link>
                ))}
              </div>
            </div>

            {/* Mobile About Us Section */}
            <div>
              <div className="block px-3 py-2 text-base text-gray-900 font-medium">About Us</div>
              <div className="ml-4 mt-2 space-y-1 border-l pl-4">
                <Link to="/about" className={`block px-3 py-2 text-sm text-gray-600 hover:text-blue-600 hover:bg-gray-50 rounded-md transition-colors ${isActive('/about') ? 'text-blue-600 bg-blue-50' : ''}`} onClick={closeMenu}>
                  About Minipore
                </Link>
                <Link to="/culture" className={`block px-3 py-2 text-sm text-gray-600 hover:text-blue-600 hover:bg-gray-50 rounded-md transition-colors ${isActive('/culture') ? 'text-blue-600 bg-blue-50' : ''}`} onClick={closeMenu}>
                  Our Culture
                </Link>
              </div>
            </div>

            <Link to="/contact" className={`block px-3 py-2 text-base text-gray-700 hover:text-blue-600 hover:bg-gray-50 rounded-md transition-colors ${isActive('/contact') ? 'text-blue-600 bg-blue-50 font-medium' : ''}`} onClick={closeMenu} >
              Contact
            </Link>
          </div>
        </div>
      </nav>
    </header>
  );
};

export default Header;